import { BrowserSession, initializeSdk, UcsSdk } from '@brandfolder/ucs-sdk-browser';
import React, { FC, useEffect, useState, useCallback, useRef } from 'react';

import fetchJson from '@api/api_helper';
import { COMPANION_SANDBOX_URL, getValidIdentityToken } from '@helpers/uppy';

import { UppyUploaderProps } from '../types/UppyTypes';

import { UppyUcsUploader } from './UppyUCSUploader';

const originalFetch = window.fetch;

/**
 * Fetches a browser session for UCS uploads from the Boulder API.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.bearerToken - Bearer token for authentication
 * @param {string} options.url - API endpoint URL
 * @param {boolean} [options.isGuestUpload] - Whether this is a guest upload
 * @param {string} [options.uploadKey] - Upload key for guest uploads
 * @returns {Promise<BrowserSession>} Browser session object
 */

const fetchBoulderUcsBrowserSession = async (options: {
  bearerToken: string;
  url: string;
  isGuestUpload?: boolean;
  uploadKey?: string;
  printuiAssetKey?: string;
  printuiDigestKey?: string;
}): Promise<BrowserSession> => {

  try {
    // Determine the request body based on whether it's a guest upload
    const body = options.isGuestUpload ? {
      upload_key: options.uploadKey,
      bf_token: BFG.BF_Token
    } : {
      resource_type: BFG?.resource?.type,
      resource_key: BFG?.resource?.key,
      ...(options.printuiAssetKey && { printui_asset_key: options.printuiAssetKey }), // eslint-disable-line @typescript-eslint/naming-convention
      ...(options.printuiDigestKey && { printui_digest_key: options.printuiDigestKey }), // eslint-disable-line @typescript-eslint/naming-convention
    };

    const response = await fetchJson(options.url, {
      method: 'POST',
      queuePriorityHigh: false,
      body,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${options.bearerToken}`,
      },
    });
    if (!response) {
      throw new Error(`Failed to create browser session: ${response}`);
    }

    return response;
  } catch (error) {
    console.error(error);
  }
};

export const UppyTusWrapper: FC<UppyUploaderProps> = (uppyProps) => {
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bearerToken = BFG.BF_Token;
  const isReinitializingRef = useRef(false);
  const ucsSdkRef = useRef<UcsSdk | null>(null);

  const createBrowserSessionFunction = useCallback((): Promise<BrowserSession> => {
    const isGuestUpload = uppyProps.guestUpload;
    const isPrintuiUpload = !!uppyProps.printuiAssetKey;

    const url = isGuestUpload || isPrintuiUpload
      ? `${BFG.domain}/api/v4/ucs/v2/guest_browser_session`
      : `${BFG.domain}/api/v4/ucs/v2/browser_session`;

    return fetchBoulderUcsBrowserSession({
      bearerToken,
      url,
      isGuestUpload,
      uploadKey: isGuestUpload ? uppyProps.uploadLinkKey : undefined,
      printuiAssetKey: uppyProps.printuiAssetKey,
      printuiDigestKey: uppyProps.printuiDigestKey,
    });
  }, [bearerToken, uppyProps.guestUpload, uppyProps.uploadLinkKey, uppyProps.printuiAssetKey, uppyProps.printuiDigestKey]);

  /**
   * Function to initialize or reinitialize the UCS SDK.
   */
  const initializeTusEndpoint = useCallback(async (): Promise<void> => {
    try {
      if (!bearerToken) {
        throw new Error('Either bearerToken or isFl2 flag is required');
      }

      setIsLoading(true);
      setError(null);

      const ucsSdk = initializeSdk({
        createBrowserSessionFunction,
      });

      let ucsTusUrl = '';
      try {
        ucsTusUrl = (await ucsSdk.getTusUrl()) || '';
        ucsSdkRef.current = ucsSdk;
        setEndpoint(ucsTusUrl);
      } catch (err) {
        console.error('Failed to get TUS URL:', err);
      }

    } catch (err) {
      console.error('Failed to initialize TUS endpoint:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [bearerToken, createBrowserSessionFunction]);

  const reinitializeSdk = useCallback(async (): Promise<void> => {
    // Prevent multiple simultaneous reinitializations
    if (isReinitializingRef.current) return;

    try {
      isReinitializingRef.current = true;
      await initializeTusEndpoint();
    } catch (err) {
      console.error('Failed to reinitialize SDK:', err);
    } finally {
      isReinitializingRef.current = false;
    }
  }, [initializeTusEndpoint]);

  // Initial SDK initialization
  useEffect(() => {
    initializeTusEndpoint();
  }, [initializeTusEndpoint]);

  useEffect(() => {
    /**
     * Monkey-patching window.fetch to simulate onBeforeRequest behavior
     * for third-party uploads in Uppy since this feature is not supported natively.
     */
    const patchedFetch = (async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
      const isCompanionRequest = typeof input === 'string' && input.includes(COMPANION_SANDBOX_URL);

      if (isCompanionRequest) {
        try {
          const identityToken = await getValidIdentityToken(ucsSdkRef, reinitializeSdk);

          init.headers = {
            ...(init.headers || {}),
            Authorization: `Bearer ${identityToken}`
          };
        } catch (err) {
          console.error('Failed to fetch token:', err);
        }
      }

      return originalFetch(input, init);
    }) as typeof fetch;

    window.fetch = patchedFetch;

    return (): void => {
      window.fetch = originalFetch;
    };
  }, [reinitializeSdk]);

  if (isLoading) {
    return <div>Loading uploader...</div>;
  }

  if (error) {
    return <div>Error initializing uploader: {error}</div>;
  }

  if (!endpoint) {
    return <div>Unable to initialize uploader endpoint</div>;
  }

  return (
    <UppyUcsUploader
      {...uppyProps}
      endpoint={endpoint}
      reinitializeSdk={reinitializeSdk}
      ucsSdkRef={ucsSdkRef}
    />
  );
};
